// useS3Upload.ts
import axios, { AxiosError } from "axios";
import * as Sentry from "@sentry/nextjs";
import { useCallback } from "react";
import slugify from "slugify";
import { v4 as uuidv4 } from "uuid";

type S3Params = {
  file: File;
  bucket: string;
  prefix?: string;
  [key: string]: any;
};

type S3Upload = (
  params: S3Params,
  handleProgress: (progress: { loaded: number; total: number }) => void
) => Promise<{ key: string; url: string }>;

export class S3UploadError extends Error {
  public cause?: Error;
  public stage: 'presign' | 'upload';
  public context: Record<string, any>;

  constructor(message: string, stage: 'presign' | 'upload', cause?: Error, context: Record<string, any> = {}) {
    super(message);
    this.name = 'S3UploadError';
    this.stage = stage;
    this.cause = cause;
    this.context = context;
  }
}

export function ensureUrlEncoded(filename: string): string {
  if (!filename) return "";
  // decode, then re-encode
  const decoded = decodeURIComponent(filename);
  const reEncoded = encodeURIComponent(decoded);

  // if re-encoded matches original, it means original was already properly encoded
  // otherwise, we return the newly encoded version
  return reEncoded === filename ? filename : reEncoded;
}

function extractAxiosErrorDetails(error: unknown): Record<string, any> {
  if (error instanceof AxiosError) {
    return {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
      message: error.message,
      code: error.code,
    };
  }
  
  if (error instanceof Error) {
    return { message: error.message, name: error.name };
  }
  
  return { unknownError: String(error) };
}

export const useS3Upload = (): { upload: S3Upload } => {
  const upload: S3Upload = useCallback(async (params, handleProgress) => {
    const { file, prefix = "", bucket } = params;
    const uniqueFileName = `${uuidv4()}-${slugify(file.name, { lower: true })}`;
    const fileInfo = { 
      name: file.name, 
      type: file.type, 
      size: file.size,
      bucket,
      prefix,
      uniqueFileName
    };

    try {
      // Step 1: Request pre-signed URL from the server
      const response = await axios.post("/api/authenticated/get-presigned-url", {
        fileName: uniqueFileName,
        fileType: file.type,
        fileSize: file.size,
        prefix,
        bucket,
      });

      if (response.status !== 200) {
        const error = new S3UploadError(
          "Failed to get pre-signed URL", 
          'presign', 
          undefined, 
          { 
            responseStatus: response.status, 
            responseData: response.data,
            ...fileInfo
          }
        );
        Sentry.captureException(error);
        throw error;
      }

      const { signedUrl, key, url } = response.data;

      console.log({ key, url });

      try {
        // Step 2: Upload the file directly to S3 using the pre-signed URL
        await axios.put(signedUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent) => {
            handleProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total || file.size,
            });
          },
        });

        return { key, url };
      } catch (uploadError: unknown) {
        const errorDetails = extractAxiosErrorDetails(uploadError);
        const contextualError = new S3UploadError(
          "Failed to upload file to S3", 
          'upload', 
          uploadError instanceof Error ? uploadError : undefined,
          {
            ...errorDetails,
            signedUrl,
            ...fileInfo
          }
        );
        
        Sentry.captureException(contextualError, {
          tags: {
            uploadStage: 'S3 PUT',
            fileType: file.type,
            fileSize: file.size,
          },
          extra: {
            ...fileInfo,
            errorDetails
          }
        });
        
        throw contextualError;
      }
    } catch (presignError: unknown) {
      // If this isn't already a S3UploadError from our own code
      if (!(presignError instanceof S3UploadError)) {
        const errorDetails = extractAxiosErrorDetails(presignError);
        const contextualError = new S3UploadError(
          "Failed to get pre-signed URL", 
          'presign', 
          presignError instanceof Error ? presignError : undefined,
          {
            ...errorDetails,
            ...fileInfo
          }
        );
        
        Sentry.captureException(contextualError, {
          tags: {
            uploadStage: 'Presign URL',
            fileType: file.type,
            fileSize: file.size,
          },
          extra: {
            ...fileInfo,
            errorDetails
          }
        });
        
        throw contextualError;
      }
      
      throw presignError;
    }
  }, []);

  return { upload };
};
